/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

export interface B2CToken {
  account: AuthAccount;
  idToken: string;
  idTokenClaims: IdTokenClaims;
  accessToken: string;
  state: string;
}

export interface BasicDigitalFarmerAccount extends AccountApiListItem {
  lastViewedAt?: Date;
}

export interface DigitalFarmerAccountDictionary {
  [index: string]: BasicDigitalFarmerAccount;
}

export interface RelationshipOwner {
  fullName?: string;
  email: string;
  phone?: string;
}

export interface DigitalFarmer {
  accounts: DigitalFarmerAccountDictionary;
  isTfo: boolean;
  selectedAccountNumber: string;
  selectedAccount?: DigitalFarmerAccount;
}

export interface AuthAccount {
  homeAccountId: string;
  environment: string;
  tenantId: string;
  username: string;
  localAccountId: string;
  idTokenClaims: IdTokenClaims;
  digitalFarmer?: DigitalFarmer;
}

export interface AccountDictionary {
  [index: string]: AuthAccount;
}
export interface IdTokenClaims {
  account_number?: string;
  accountName?: string;
  ver: string;
  iss: string;
  sub: string;
  aud: string;
  exp: number;
  nonce: string;
  emails: string;
  username: string;
  iat: number;
  auth_time: number;
  given_name: string;
  family_name: string;
  tfp: string;
  nbf: number;
}

export interface AuthState {
  accounts: AccountDictionary;
  listStatus: string;
  detailsStatus: string;
  userDetailsStatus: string;
  isCEUser: boolean;
  selectedAccountId: string | null;
  userDetails: IAccountsContactDetails | null;
  atomicJWTToken: string | null;
  token: string | null;
}

export interface AccountAddress {
  city: string;
  county: string;
  dairySupplyNum: string;
  fullAddress: string;
  name: string;
  ruralDeliveryNum: string;
  street: string;
  streetNumber: string;
  zipCode: string;
  recordId?: number;
}

export interface Contact {
  name: string;
  email: string;
  phone: string;
}

export interface DigitalFarmerAccount {
  /**
   * Name of farm / business on the account
   */
  accountName: string;

  /**
   * Account number for the account
   */
  accountNumber: string;

  /**
   * Legal Name for the account
   */
  legalName: string;

  /**
   * Primary Phone Number for the account
   */
  primaryPhoneNumber: IDigitalFarmerAccountPrimaryPhone | null;

  /**
   * Email Address for the account
   */
  emailAddresses: IDigitalFarmerAccountEmail[];

  /**
   * List of all known addresses for the account
   */
  addresses: AccountAddress[];

  /**
   * Managed accounts only
   */
  hasTfoAccess?: boolean;

  /**
   * Managed accounts only
   */
  contact?: Contact | null;

  /**
   * @deprecated The field should not be used
   */
  customerName?: string;

  /**
   * Calculated on backend. Disables cart access
   */
  hasCheckoutAccess: boolean;

  /**
   * Calculated on backend. Disables application access
   */
  hasShareholderAccess: boolean;

  /**
   * Default store used for the account
   */
  homeBranchId: string;

  /**
   * Default Port for nutrition promo
   */
  defaultPort: {
    name: string;
    island: string;
  };

  /**
   * Default selected account on first use
   */
  isDefault: boolean;

  /**
   * Tfo contact details
   */
  relationshipOwner?: RelationshipOwner;

  /**
   * Enforce customer refference
   */
  captureCustomerReference: boolean;
}

export interface IDigitalFarmerAccountPrimaryPhone {
  isMobile: boolean | null;
  phoneNumber: string | null;
}

export interface IDigitalFarmerAccountEmail {
  isPrimary: boolean | null;
  isStatementPurpose: boolean | null;
  email: string | null;
}

export type AccountApiResponse = {
  account: DigitalFarmerAccount;
};

export type UserDetailsApiResponse = IAccountsContactDetails;
export interface AccountListApiResponse {
  accounts: AccountApiListItem[];
}

export interface AccountApiListItem {
  accountNumber: string;
  accountName: string;
  accountEmail: string | null;
  isActive: boolean;
  isDefaultAccountForUser: boolean;
  hasTfoAccess: boolean;
}

export interface IAccountsContactDetails {
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
  dob: string | null;
  primaryPhone: string | null;
  primaryEmail: string | null;
}

export interface AtomicTokenApiResponse {
  token: string;
}
